
import Vue from 'vue';
import { SideBar } from '../SideBar';
import { CvButtonSet, CvButton, CvCheckbox } from '@carbon/vue';
import {
  Artwork,
  ArtworkResult,
  w2pFileUploader,
} from '@/components/w2pFileUploader';
import FileService from '@/services/FileService';
import { validateFn } from '@/validators/basicFileValidator';
import { supportedExtensions } from '@/constants/supportedExtensions';
import { base64StringToBlob } from 'blob-util';
import { ErrorNotification } from '../../../libs/proof-approval/ErrorNotification';

export default Vue.extend({
  components: {
    CvButtonSet,
    CvButton,
    CvCheckbox,
    SideBar,
    w2pFileUploader,
    ErrorNotification,
  },
  props: {
    customerId: {
      type: String,
      required: true,
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    resetState: {
      type: Boolean,
      required: false,
      default: false,
    },
    context: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      supportedExtensions,
      actionBtnEnabled: false,
      api: FileService,
      uploadFn: null as unknown as () => Promise<ArtworkResult[]>,
      isFileUploaderEnabled: true,
      validateFn,
      errorMessage: '',
      isNotificationVisible: false,
    };
  },
  computed: {
    uploadOptions(): Artwork {
      return {
        externalCustomerId: this.sapCustomerId,
        customerId: this.customerId,
        title: '',
        shouldSaveInLogoLibrary: this.context === 'myAccount',
        shouldSaveDraftForWizard: this.context === 'wizard',
      } as Artwork;
    },
  },
  watch: {
    isOpen() {
      if (!this.isOpen) this.resetFlags();
    },

    resetState() {
      if (this.resetState) {
        this.uploadOptions.shouldSaveInLogoLibrary = false;
      }
    },
  },
  methods: {
    onChange(files: Artwork[]) {
      this.actionBtnEnabled = files.length > 0;
      this.isFileUploaderEnabled = files.length === 0;
      return files.length === 0;
    },
    onUploadError() {
      this.resetFlags();
    },
    onUploadReady({
      files,
      up,
    }: {
      files: Artwork[];
      up: (files: Artwork[]) => Promise<ArtworkResult[]>;
    }) {
      this.uploadFn = () => up(files);
    },
    onUploadClick() {
      this.actionBtnEnabled = false;
      this.uploadFn()
        .then((response) => {
          if (this.context === 'myAccount') {
            setTimeout(() => {
              this.$emit('action', 'upload-success');
            }, 1000);
          }

          if (this.context === 'wizard') {
            const blob = base64StringToBlob(response[0].pngFile, 'image/png');
            const otherBlob = response[0].traditionalTechniquePngFile
              ? base64StringToBlob(
                  response[0].traditionalTechniquePngFile,
                  'image/png'
                )
              : '';

            let payload = {
              ...response[0],
              id: response[0].personalizationArtworkId,
              title: response[0].fileName.replace(/\.[^.]+$/, ''),
              customerId: this.uploadOptions.customerId,
              pngFile: blob,
              pngFileUrl: URL.createObjectURL(blob),
              traditionalTechniquePngFile: otherBlob,
            };
            this.$emit('payload:ready', payload);
          }
        })
        .catch((error) => {
          console.log(error);
          if (error.response.status === 409) {
            if (
              error.response.data.error.message ===
              'Provided PDF contains more than 10 pages, which is not allowed. Please provide PDF with less than 10 pages.'
            ) {
              this.errorMessage = this.$t(
                'section.errors.mutlipage-pdf-exception'
              ).toString();
            } else if (
              error.response.data.error.message ===
              'W2P is unable to process provided artwork. Processing would require too much time. Please provide simplified version.'
            ) {
              this.errorMessage = this.$t(
                'section.errors.timeout-exception'
              ).toString();
            } else if (
              error.response.data.error.message ===
              'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
            ) {
              this.errorMessage = this.$t(
                'section.errors.image-magick'
              ).toString();
            } else if (
              error.response.data.error.message ===
              'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
            ) {
              this.errorMessage = this.$t(
                'section.errors.ghost-script'
              ).toString();
            } else if (
              error.response.data.error.message ===
              'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
            ) {
              this.errorMessage = this.$t(
                'section.errors.resolution-error'
              ).toString();
            } else if (
              error.response.data.error.message ===
              'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
            ) {
              this.errorMessage = this.$t(
                'section.errors.argument-exception'
              ).toString();
            }
            this.isNotificationVisible = true;
          }
        })
        .finally(() => {
          this.resetFlags();
        });
    },
    onCancel() {
      this.$emit('upload-sidebar:toggle', false);
    },
    resetFlags() {
      this.actionBtnEnabled = false;
      this.isFileUploaderEnabled = true;
    },
  },
});
